import Vue from 'vue';
import axios from "axios";
import Utils from "./utils";
import TokenService from '../../common/token.service';
import { updateLastSearchTerminologies } from '../../common/historyUtils.js';

class TerminologiesStore {

    constructor() {
        this.state = {
            page: 1,
            itemsPerPage: 10,
            terminologiesList: [],
            extendedSearch: false,
            numberOfTerminologies: 10,
            errorInQuery: false,
            searchType: 'classic',
        };
    }

    /**
     * Rafraîchit le token utilisateur s'il est expiré.
     * Utilise le TokenService pour vérifier et renouveler le token d'accès de l'utilisateur.
     * @returns {Promise<void>}
     */
    async refreshUserTokenIfExpired() {
        await TokenService.refreshUserTokenIfExpired(Vue.prototype.$ansAdminTerminologies)
    }

    /**
     * Construit l'en-tête d'autorisation Bearer pour les requêtes API sécurisées.
     * @returns {Object} Un objet contenant l'en-tête Authorization avec le token d'accès courant.
     */
    buildAuthorizationHeader() {
        return { 'Authorization': 'Bearer ' + Vue.prototype.$ansAdminTerminologies.tokens.accessToken };
    }
    /**
     * Définit la page actuelle pour la pagination.
     * @param {number} page - Le numéro de page
     */
    setPage(page) {
        this.state.page = page;
    }

    /**
     * Définit le nombre d'éléments par page.
     * @param {number} itemsPerPage - Le nombre d'éléments à afficher par page
     */
    setItemsPerPage(itemsPerPage) {
        this.state.itemsPerPage = itemsPerPage;
    }

    /**
     * Définit le type de recherche.
     * @param {string} searchType - Le type de recherche ('classic' par défaut)
     */
    setSearchType(searchType) {
        this.state.searchType = searchType;
    }

    /**
     * Récupère l'état de la recherche étendue.
     * @returns {boolean} L'état de la recherche étendue
     */
    getExtendedSearch() {
        return this.state.extendedSearch;
    }

    /**
     * Définit l'état de la recherche étendue.
     * @param {boolean} extendedSearch - Active ou désactive la recherche étendue
     */
    setExtendedSearch(extendedSearch) {
        this.state.extendedSearch = extendedSearch;
    }


    /**
     * Remet à zéro les résultats de recherche
     */
    resetSearchResults() {
        this.state.page = 1;
        this.state.errorInQuery = false;
        this.state.terminologiesList = [];
        this.state.numberOfTerminologies = 0;
    }

    /**
     * Récupère les données depuis l'API et met à jour l'état du store.
     * @param {string} endpoint - L'endpoint de l'API à appeler
     * @param {Object} params - Les paramètres de la requête
     */
    async getDataFromApi(endpoint, params) {
        // Réinitialiser la page à 1 pour une nouvelle recherche
        this.state.page = 1;
        this.state.errorInQuery = false;
        this.state.terminologiesList = [];
        this.state.numberOfTerminologies = 0;
        
        const axiosParams = Utils.buildCustomParamsForAxios(params);
        
        await axios.get(
            Vue.prototype.$searchResultManager.middlewareBaseUrl + endpoint + '?' + axiosParams
        ).then((response) => { // Handle success
            this.state.terminologiesList = [...response.data.terminologies];
            this.state.numberOfTerminologies = response.data.numberOfTerminologies;
            
            // Mettre à jour l'historique avec le nombre de terminologies trouvées
            if (response.data.numberOfTerminologies !== undefined) {
                updateLastSearchTerminologies(response.data.numberOfTerminologies);
            }
        }).catch(() => {
            this.state.errorInQuery = true;
            this.state.terminologiesList = [];
            this.state.numberOfTerminologies = 0;
        });
    }

    /**
     * Charge la page suivante de terminologies et les ajoute à la liste existante.
     * @param {string} endpoint - L'endpoint de l'API à appeler
     * @param {Object} params - Les paramètres de la requête
     */
    async loadMoreTerminologies(endpoint, params) {
        this.state.errorInQuery = false;
        const axiosParams = Utils.buildCustomParamsForAxios(params);
        
        await axios.get(
            Vue.prototype.$searchResultManager.middlewareBaseUrl + endpoint + '?' + axiosParams
        ).then((response) => {
            // Fusionner les nouvelles terminologies avec la liste existante
            this.state.terminologiesList = [
                ...this.state.terminologiesList,
                ...response.data.terminologies
            ];
            this.state.numberOfTerminologies = response.data.numberOfTerminologies;
        }).catch(() => {
            this.state.errorInQuery = true;
            this.state.terminologiesList = [];
            this.state.numberOfTerminologies = 0;
        });
    }

}

export default new TerminologiesStore();
